import { forwardRef } from "react";

const PurchaseContract = forwardRef(({ vehicle, buyer, sellerInfo, options = {} }, ref) => {
  const today = new Date().toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit', 
    year: 'numeric'
  });

  const defaultSeller = {
    name: "Auto Thurm",
    address: "Wildenreuth A 55",
    city: "92681 Erbendorf",
    phone: "09682 722",
    email: "info@auto-thurm.de",
    taxId: "DE123456789",
    commercialRegister: "HRB 12345, Amtsgericht Weiden i.d. OPf."
  };

  const seller = { ...defaultSeller, ...sellerInfo };

  // Calculate prices
  const basePrice = vehicle?.price || 0;
  const warrantyPrice = options.warranty24Months ? 599 : (options.warranty12Months ? 349 : 0);
  const inspectionPrice = options.newInspection ? 89 : 0;
  const winterTiresPrice = options.winterTires ? (options.winterTiresPrice || 0) : 0;
  const accessoriesPrice = options.accessories ? (options.accessoriesPrice || 0) : 0;
  const tradeInValue = options.tradeIn ? (options.tradeInValue || 0) : 0;
  const totalPrice = basePrice + warrantyPrice + inspectionPrice + winterTiresPrice + accessoriesPrice - tradeInValue;
  const vatAmount = totalPrice * 0.19 / 1.19;
  const netPrice = totalPrice - vatAmount;

  // Print styles
  const pageStyle = {
    width: '210mm',
    minHeight: '297mm',
    padding: '15mm',
    backgroundColor: 'white',
    color: 'black',
    fontFamily: 'serif',
    fontSize: '11px',
    lineHeight: '1.4',
    position: 'relative'
  };

  // Watermark logo URL
  const logoUrl = `${window.location.origin}/img/renault-logo-vertrag.png`;

  return (
    <div ref={ref} className="bg-white text-black font-serif" style={pageStyle}>
      {/* Print-specific styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 15mm;
          }
          .print-page-break {
            page-break-before: always;
            break-before: page;
          }
          .print-avoid-break {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .print-page {
            page-break-after: always;
            break-after: page;
          }
          .watermark-logo {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      {/* Watermark Logo - fixed background on each page */}
      <div 
        className="watermark-logo"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          height: 'auto',
          opacity: 0.07,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        <img 
          src={logoUrl} 
          alt="" 
          style={{ 
            width: '100%', 
            height: 'auto',
            filter: 'invert(1) brightness(0.3)'
          }} 
        />
      </div>

      {/* ========== SEITE 1: Vertragsparteien & Fahrzeugdaten ========== */}
      <div className="print-avoid-break relative" style={{ zIndex: 1 }}>
        {/* Header */}
        <div className="text-center border-b-2 border-black pb-3 mb-4">
          <h1 className="text-xl font-bold uppercase tracking-wide">Kaufvertrag</h1>
          <p className="text-xs text-gray-600 mt-1">über ein Kraftfahrzeug (Gebrauchtwagen)</p>
          <p className="text-xs text-gray-500">gemäß §§ 433 ff. BGB</p>
        </div>

        {/* Contract Number & Date */}
        <div className="flex justify-between mb-4 text-xs">
          <div>
            <strong>Vertragsnummer:</strong> KV-{vehicle?.id?.substring(0, 8).toUpperCase() || 'XXXXXX'}
          </div>
          <div>
            <strong>Ort, Datum:</strong> Erbendorf, {today}
          </div>
        </div>
      </div>

      {/* §1 Parties Section */}
      <div className="mb-5 print-avoid-break relative" style={{ zIndex: 1 }}>
        <h2 className="font-bold text-xs mb-2 bg-gray-100 p-2">§ 1 Vertragsparteien</h2>
        <div className="grid grid-cols-2 gap-4 text-xs">
          {/* Seller */}
          <div className="border p-2">
            <p className="font-bold mb-1">Verkäufer:</p>
            <p>{seller.name}</p>
            <p>{seller.address}</p>
            <p>{seller.city}</p>
            <p>Tel: {seller.phone}</p>
            <p>E-Mail: {seller.email}</p>
            <p className="mt-1 text-gray-600 text-xs">USt-IdNr.: {seller.taxId}</p>
          </div>

          {/* Buyer */}
          <div className="border p-2">
            <p className="font-bold mb-1">Käufer:</p>
            <p>{buyer?.name || '___________________________'}</p>
            <p>{buyer?.address || '___________________________'}</p>
            <p>{buyer?.city || '___________________________'}</p>
            <p>Tel: {buyer?.phone || '___________________________'}</p>
            <p>E-Mail: {buyer?.email || '___________________________'}</p>
            <p className="mt-1">Ausweis-Nr.: {buyer?.idNumber || '___________________'}</p>
          </div>
        </div>
      </div>

      {/* §2 Vehicle Details */}
      <div className="mb-5 print-avoid-break relative" style={{ zIndex: 1 }}>
        <h2 className="font-bold text-xs mb-2 bg-gray-100 p-2">§ 2 Kaufgegenstand (Fahrzeugdaten)</h2>
        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs border p-2">
          <p><strong>Hersteller/Marke:</strong> {vehicle?.brand || '_______________'}</p>
          <p><strong>Fahrzeugtyp/Modell:</strong> {vehicle?.model || '_______________'}</p>
          <p><strong>Börsentyp:</strong> {vehicle?.boersentyp || `${vehicle?.brand || ''} ${vehicle?.model || ''} ${vehicle?.engine_power_ps ? vehicle.engine_power_ps + ' PS' : ''}`.trim() || '_______________'}</p>
          <p><strong>Fahrzeug-Ident.-Nr. (VIN):</strong> {vehicle?.vin || '___________________________'}</p>
          <p><strong>HSN/TSN:</strong> {vehicle?.hsn || '____'} / {vehicle?.tsn || '___'}</p>
          <p><strong>Erstzulassung:</strong> {vehicle?.first_registration || vehicle?.year || '_______________'}</p>
          <p><strong>Kilometerstand:</strong> {vehicle?.mileage?.toLocaleString('de-DE') || '_______________'} km</p>
          <p><strong>Leistung:</strong> {vehicle?.engine_power_kw || '___'} kW / {vehicle?.engine_power_ps || '___'} PS</p>
          <p><strong>Hubraum:</strong> {vehicle?.engine_displacement?.toLocaleString('de-DE') || '___'} ccm</p>
          <p><strong>Kraftstoff:</strong> {vehicle?.fuel_type || '_______________'}</p>
          <p><strong>Getriebe:</strong> {vehicle?.transmission || '_______________'}</p>
          <p><strong>Antrieb:</strong> {vehicle?.drive_type || '_______________'}</p>
          <p><strong>Farbe:</strong> {vehicle?.color || '_______________'}</p>
          <p><strong>Fahrzeugklasse:</strong> {vehicle?.vehicle_type || '_______________'}</p>
          <p><strong>Türen/Sitze:</strong> {vehicle?.doors || '___'} / {vehicle?.seats || '___'}</p>
          <p><strong>Schadstoffklasse:</strong> {vehicle?.emission_class || '_______________'}</p>
          <p><strong>CO₂-Emission:</strong> {vehicle?.co2_emission || '___'} g/km</p>
          <p><strong>Verbrauch:</strong> {vehicle?.fuel_consumption || '___'} l/100km</p>
          <p><strong>Leergewicht:</strong> {vehicle?.weight?.toLocaleString('de-DE') || '___'} kg</p>
          <p><strong>Nächste HU/AU:</strong> {vehicle?.next_inspection || '_______________'}</p>
          <p><strong>Anzahl Vorbesitzer:</strong> {vehicle?.previous_owners || '___'}</p>
          <p><strong>Amtl. Kennzeichen:</strong> {vehicle?.license_plate || '_______________'}</p>
          <p><strong>Schlüsselanzahl:</strong> {vehicle?.key_count || '2'} Stück</p>
        </div>
        {vehicle?.features && vehicle.features.length > 0 && (
          <div className="mt-2 text-xs border p-2">
            <strong>Sonderausstattung:</strong> {vehicle.features.join(', ')}
          </div>
        )}
      </div>

      {/* §3 Purchase Price */}
      <div className="mb-5 print-avoid-break relative" style={{ zIndex: 1 }}>
        <h2 className="font-bold text-xs mb-2 bg-gray-100 p-2">§ 3 Kaufpreis</h2>
        <div className="text-xs border p-2">
          <table className="w-full">
            <tbody>
              <tr>
                <td className="py-0.5">Fahrzeugpreis:</td>
                <td className="text-right">{basePrice.toLocaleString('de-DE', {minimumFractionDigits: 2})} €</td>
              </tr>
              {options.warranty24Months && (
                <tr>
                  <td className="py-0.5">+ Garantieverlängerung (24 Monate):</td>
                  <td className="text-right">599,00 €</td>
                </tr>
              )}
              {options.warranty12Months && !options.warranty24Months && (
                <tr>
                  <td className="py-0.5">+ Garantieverlängerung (12 Monate):</td>
                  <td className="text-right">349,00 €</td>
                </tr>
              )}
              {options.newInspection && (
                <tr>
                  <td className="py-0.5">+ Neue HU/AU-Prüfung:</td>
                  <td className="text-right">89,00 €</td>
                </tr>
              )}
              {options.winterTires && winterTiresPrice > 0 && (
                <tr>
                  <td className="py-0.5">+ Winterreifen/Kompletträder:</td>
                  <td className="text-right">{winterTiresPrice.toLocaleString('de-DE', {minimumFractionDigits: 2})} €</td>
                </tr>
              )}
              {options.accessories && accessoriesPrice > 0 && (
                <tr>
                  <td className="py-0.5">+ Zubehör ({options.accessoriesDescription || 'diverse'}):</td>
                  <td className="text-right">{accessoriesPrice.toLocaleString('de-DE', {minimumFractionDigits: 2})} €</td>
                </tr>
              )}
              {options.tradeIn && tradeInValue > 0 && (
                <tr className="text-green-700">
                  <td className="py-0.5">- Inzahlungnahme ({options.tradeInVehicle || 'Altfahrzeug'}):</td>
                  <td className="text-right">-{tradeInValue.toLocaleString('de-DE', {minimumFractionDigits: 2})} €</td>
                </tr>
              )}
              <tr className="border-t font-bold">
                <td className="py-1">Gesamtkaufpreis (brutto):</td>
                <td className="text-right text-base">{totalPrice.toLocaleString('de-DE', {minimumFractionDigits: 2})} €</td>
              </tr>
              <tr className="text-gray-600">
                <td className="text-xs">darin enthaltene USt. (19%):</td>
                <td className="text-right text-xs">{vatAmount.toLocaleString('de-DE', {minimumFractionDigits: 2})} €</td>
              </tr>
            </tbody>
          </table>
          <p className="mt-1 text-xs text-gray-600">
            (in Worten: {numberToWords(totalPrice)} Euro)
          </p>
        </div>
      </div>

      {/* ========== SEITE 2 ========== */}
      <div className="print-page-break"></div>

      {/* §4 Payment */}
      <div className="mb-5 print-avoid-break relative" style={{ zIndex: 1 }}>
        <h2 className="font-bold text-xs mb-2 bg-gray-100 p-2">§ 4 Zahlung und Übergabe</h2>
        <div className="text-xs space-y-1">
          <p><strong>Zahlungsart:</strong></p>
          <div className="flex gap-4 ml-4">
            <label className="flex items-center gap-1">
              <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.paymentCash ? '✓' : ''}</span>
              Barzahlung bei Übergabe
            </label>
            <label className="flex items-center gap-1">
              <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.paymentTransfer ? '✓' : ''}</span>
              Überweisung (vor Übergabe)
            </label>
            <label className="flex items-center gap-1">
              <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.paymentFinancing ? '✓' : ''}</span>
              Finanzierung
            </label>
          </div>
          {options.paymentFinancing && (
            <p className="ml-4 text-gray-600">Finanzierung über: {options.financingBank || '_______________'}</p>
          )}
          <p className="mt-2"><strong>Geplanter Übergabetermin:</strong> {options.deliveryDate || '_______________'}</p>
        </div>
      </div>

      {/* §5 Vehicle Condition */}
      <div className="mb-5 print-avoid-break relative" style={{ zIndex: 1 }}>
        <h2 className="font-bold text-xs mb-2 bg-gray-100 p-2">§ 5 Fahrzeugzustand und Beschaffenheit</h2>
        <div className="text-xs space-y-1">
          <p>Der Käufer hat das Fahrzeug vor Vertragsabschluss besichtigt und ggf. Probe gefahren.</p>
          
          <div className="flex gap-4 ml-4">
            <label className="flex items-center gap-1">
              <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.testDriveYes ? '✓' : ''}</span>
              Probefahrt erfolgt
            </label>
            <label className="flex items-center gap-1">
              <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.testDriveNo ? '✓' : ''}</span>
              Keine Probefahrt
            </label>
          </div>

          <p className="mt-2"><strong>Bekannte Mängel / Beschädigungen:</strong></p>
          <div className="border p-2 min-h-[30px]">
            {options.knownDefects || 'Keine bekannten Mängel.'}
          </div>

          <div className="mt-2 flex gap-4">
            <label className="flex items-center gap-1">
              <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.accidentFree ? '✓' : ''}</span>
              Unfallfrei
            </label>
            <label className="flex items-center gap-1">
              <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.accidentRepaired ? '✓' : ''}</span>
              Unfallschaden (fachgerecht repariert)
            </label>
            <label className="flex items-center gap-1">
              <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.accidentUnknown ? '✓' : ''}</span>
              Unfallhistorie unbekannt
            </label>
          </div>
        </div>
      </div>

      {/* §6 Warranty */}
      <div className="mb-5 print-avoid-break relative" style={{ zIndex: 1 }}>
        <h2 className="font-bold text-xs mb-2 bg-gray-100 p-2">§ 6 Gewährleistung und Garantie</h2>
        <div className="text-xs space-y-1">
          <p><strong>Gesetzliche Gewährleistung:</strong></p>
          <p className="ml-4 text-xs">
            Beim Verkauf von Gebrauchtwagen an Verbraucher (§ 13 BGB) gilt die gesetzliche Gewährleistung 
            von 12 Monaten ab Übergabe (§ 476 Abs. 2 BGB). Bei Verkauf an Unternehmer kann die 
            Gewährleistung ausgeschlossen werden.
          </p>

          <div className="flex gap-4 mt-1 ml-4">
            <label className="flex items-center gap-1">
              <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.buyerPrivate ? '✓' : ''}</span>
              Käufer ist Verbraucher
            </label>
            <label className="flex items-center gap-1">
              <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.buyerBusiness ? '✓' : ''}</span>
              Käufer ist Unternehmer (Gewährleistungsausschluss)
            </label>
          </div>

          {(options.warranty12Months || options.warranty24Months) && (
            <div className="mt-2 p-2 border bg-green-50">
              <p className="font-bold text-green-800">
                ✓ Zusätzliche Gebrauchtwagengarantie: {options.warranty24Months ? '24 Monate' : '12 Monate'}
              </p>
              <p className="text-green-700 text-xs mt-1">
                Umfang gem. beigefügten Garantiebedingungen. Selbstbeteiligung: 150 € pro Schadensfall.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* §7 Additional Agreements */}
      <div className="mb-5 print-avoid-break relative" style={{ zIndex: 1 }}>
        <h2 className="font-bold text-xs mb-2 bg-gray-100 p-2">§ 7 Sonstige Vereinbarungen</h2>
        <div className="text-xs border p-2 min-h-[40px]">
          {options.additionalAgreements || 'Keine weiteren Vereinbarungen.'}
        </div>
      </div>

      {/* §8 Included Items */}
      <div className="mb-5 print-avoid-break relative" style={{ zIndex: 1 }}>
        <h2 className="font-bold text-xs mb-2 bg-gray-100 p-2">§ 8 Im Kaufpreis enthaltenes Zubehör</h2>
        <div className="text-xs grid grid-cols-3 gap-1">
          <label className="flex items-center gap-1">
            <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.includeServiceBook ? '✓' : ''}</span>
            Serviceheft/Scheckheft
          </label>
          <label className="flex items-center gap-1">
            <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.includeManual ? '✓' : ''}</span>
            Betriebsanleitung
          </label>
          <label className="flex items-center gap-1">
            <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.includeSpareWheel ? '✓' : ''}</span>
            Ersatzrad/Pannenset
          </label>
          <label className="flex items-center gap-1">
            <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.includeFirstAid ? '✓' : ''}</span>
            Verbandskasten
          </label>
          <label className="flex items-center gap-1">
            <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.includeWarningTriangle ? '✓' : ''}</span>
            Warndreieck
          </label>
          <label className="flex items-center gap-1">
            <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.includeVest ? '✓' : ''}</span>
            Warnweste
          </label>
          <label className="flex items-center gap-1">
            <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.includeWinterTires ? '✓' : ''}</span>
            Winterreifen
          </label>
          <label className="flex items-center gap-1">
            <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.includeRoofRack ? '✓' : ''}</span>
            Dachträger
          </label>
          <label className="flex items-center gap-1">
            <span className="w-3 h-3 border border-black inline-block text-center text-xs leading-3">{options.includeFloorMats ? '✓' : ''}</span>
            Fußmatten
          </label>
        </div>
      </div>

      {/* ========== SEITE 3: Schlussbestimmungen & Unterschriften ========== */}
      <div className="print-page-break"></div>

      {/* §9 Legal Notice */}
      <div className="mb-5 print-avoid-break relative" style={{ zIndex: 1 }}>
        <h2 className="font-bold text-xs mb-2 bg-gray-100 p-2">§ 9 Schlussbestimmungen</h2>
        <div className="text-xs space-y-0.5">
          <p>1. Der Käufer bestätigt, dass ihm bekannt ist, dass das Fahrzeug erst nach vollständiger Bezahlung in sein Eigentum übergeht (Eigentumsvorbehalt).</p>
          <p>2. Änderungen und Ergänzungen dieses Vertrages bedürfen der Schriftform.</p>
          <p>3. Sollten einzelne Bestimmungen unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.</p>
          <p>4. Der Käufer verpflichtet sich zur unverzüglichen Ummeldung des Fahrzeugs.</p>
          <p>5. Gerichtsstand ist, soweit gesetzlich zulässig, Weiden i.d. OPf.</p>
          <p>6. Es gilt das Recht der Bundesrepublik Deutschland.</p>
        </div>
      </div>

      {/* Confirmation checkboxes */}
      <div className="mb-5 text-xs border p-2 bg-gray-50 print-avoid-break relative" style={{ zIndex: 1 }}>
        <p className="font-bold mb-2">Bestätigungen des Käufers:</p>
        <div className="space-y-0.5">
          <label className="flex items-start gap-2">
            <span className="w-3 h-3 border border-black inline-block mt-0.5 flex-shrink-0 text-center text-xs leading-3">✓</span>
            <span>Ich habe den Kaufvertrag vollständig gelesen und verstanden.</span>
          </label>
          <label className="flex items-start gap-2">
            <span className="w-3 h-3 border border-black inline-block mt-0.5 flex-shrink-0 text-center text-xs leading-3">✓</span>
            <span>Ich bestätige, dass die angegebenen Daten korrekt sind.</span>
          </label>
          <label className="flex items-start gap-2">
            <span className="w-3 h-3 border border-black inline-block mt-0.5 flex-shrink-0 text-center text-xs leading-3">✓</span>
            <span>Ich wurde über mein Widerrufsrecht bei Finanzierung informiert (falls zutreffend).</span>
          </label>
        </div>
      </div>

      {/* Signatures */}
      <div className="grid grid-cols-2 gap-6 mt-8 print-avoid-break relative" style={{ zIndex: 1 }}>
        <div className="text-center">
          <div className="border-b border-black pt-2 mt-10 mb-1"></div>
          <p className="text-xs">Ort, Datum</p>
          <div className="border-b border-black pt-2 mt-6 mb-1"></div>
          <p className="text-xs">Unterschrift Verkäufer</p>
          <p className="text-xs text-gray-500">{seller.name}</p>
          <p className="text-xs text-gray-400">(Firmenstempel)</p>
        </div>
        <div className="text-center">
          <div className="border-b border-black pt-2 mt-10 mb-1"></div>
          <p className="text-xs">Ort, Datum</p>
          <div className="border-b border-black pt-2 mt-6 mb-1"></div>
          <p className="text-xs">Unterschrift Käufer</p>
          <p className="text-xs text-gray-500">{buyer?.name || 'Käufer'}</p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 pt-3 border-t text-center text-xs text-gray-400 relative" style={{ zIndex: 1 }}>
        <p>Dieser Vertrag wurde in zwei gleichlautenden Exemplaren ausgefertigt. Jede Partei erhält ein Exemplar.</p>
        <p className="mt-1">{seller.name} • {seller.address} • {seller.city} • Tel: {seller.phone}</p>
        <p className="mt-2 text-gray-300">Seite 3 von 3</p>
      </div>
    </div>
  );
});

// Helper function to convert number to German words
function numberToWords(num) {
  const ones = ['', 'ein', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht', 'neun', 'zehn', 'elf', 'zwölf', 'dreizehn', 'vierzehn', 'fünfzehn', 'sechzehn', 'siebzehn', 'achtzehn', 'neunzehn'];
  const tens = ['', '', 'zwanzig', 'dreißig', 'vierzig', 'fünfzig', 'sechzig', 'siebzig', 'achtzig', 'neunzig'];
  
  if (num === 0) return 'null';
  
  const euros = Math.floor(num);
  const cents = Math.round((num - euros) * 100);
  
  let result = '';
  
  if (euros >= 1000) {
    const thousands = Math.floor(euros / 1000);
    if (thousands === 1) {
      result += 'eintausend';
    } else if (thousands < 20) {
      result += ones[thousands] + 'tausend';
    } else {
      const t = Math.floor(thousands / 10);
      const o = thousands % 10;
      if (o === 0) {
        result += tens[t] + 'tausend';
      } else {
        result += ones[o] + 'und' + tens[t] + 'tausend';
      }
    }
  }
  
  const remainder = euros % 1000;
  if (remainder >= 100) {
    const hundreds = Math.floor(remainder / 100);
    result += ones[hundreds] + 'hundert';
  }
  
  const lastTwo = remainder % 100;
  if (lastTwo > 0) {
    if (lastTwo < 20) {
      result += ones[lastTwo];
    } else {
      const t = Math.floor(lastTwo / 10);
      const o = lastTwo % 10;
      if (o === 0) {
        result += tens[t];
      } else {
        result += ones[o] + 'und' + tens[t];
      }
    }
  }
  
  if (cents > 0) {
    result += ' ' + cents + '/100';
  }
  
  return result.charAt(0).toUpperCase() + result.slice(1);
}

PurchaseContract.displayName = 'PurchaseContract';

export default PurchaseContract;
